import * as React from "react";
import MuiTextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { TextFieldVariant } from "../TextField";

export type ComboBoxProps<T> = {
  id?: string;
  options: T[];
  value: T | null;
  onChange: (value: T | null) => void;
  getOptionLabel: (option: T) => string;
  // How to tell the chosen option apart in the list, when options are not the same objects as the value.
  isOptionEqual?: (option: T, value: T) => boolean;
  // No clear button, so a chosen value cannot be set back to nothing.
  disableClearable?: boolean;
  // Highlight the first match while typing, so Enter picks it.
  autoHighlight?: boolean;
  disabled?: boolean;
  // Draws the open list's panel, e.g. a Surface in the form's colours.
  listComponent?: React.ComponentType<React.HTMLAttributes<HTMLElement>>;
  label?: React.ReactNode;
  variant?: TextFieldVariant;
  fullWidth?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  shrinkLabel?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

// A text field that filters a list of options as you type.
export const ComboBox = <T,>({
  onChange,
  isOptionEqual,
  listComponent,
  label,
  variant = TextFieldVariant.OUTLINED,
  fullWidth,
  required,
  error,
  helperText,
  placeholder,
  shrinkLabel,
  ...rest
}: ComboBoxProps<T>) => (
  <Autocomplete<T, false, boolean, false>
    onChange={(_event, next) => onChange(next as T | null)}
    getOptionSelected={isOptionEqual}
    {...(listComponent ? { PaperComponent: listComponent } : {})}
    renderInput={(params) => (
      <MuiTextField
        {...params}
        label={label}
        variant={variant as any}
        fullWidth={fullWidth}
        required={required}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        InputLabelProps={shrinkLabel ? { ...params.InputLabelProps, shrink: true } : params.InputLabelProps}
      />
    )}
    {...rest}
  />
);
